import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StyledSelect } from '@/components/ui/styled-select';
import {
  Users, Building2, DollarSign, TrendingUp, UserCheck,
  Search, Plus, X, Phone,
  ShoppingBag, FileText, ListPlus,
  BarChart3, Receipt, UserPlus, MessageSquare, Globe, Shield
} from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';

// Tab components
import { AdminDashboardTab, AdminAnalyticsTab } from '@/components/admin/AdminDashboardTab';
import { AdminUsersTab, AdminWaitlistTab } from '@/components/admin/AdminUsersTab';
import { AdminLeadsTab, AdminPartnersTab, AdminBillingTab, AdminCountriesTab } from '@/components/admin/AdminLeadsTab';
import { AdminPropertiesTab, AdminMarketplaceTab, AdminTransactionsTab, AdminConciergeTab } from '@/components/admin/AdminContentTab';
import { AdminCountryStatusTab } from '@/components/admin/AdminCountryStatusTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'waitlist', label: 'Waitlist', icon: ListPlus },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'leads', label: 'Leads', icon: Phone },
  { id: 'billing', label: 'Billing', icon: Receipt },
  { id: 'partners', label: 'Partners', icon: UserCheck },
  { id: 'properties', label: 'Properties', icon: Building2 },
  { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
  { id: 'transactions', label: 'Transactions', icon: DollarSign },
  { id: 'concierge', label: 'Concierge', icon: MessageSquare },
  { id: 'countries', label: 'Country Settings', icon: Globe },
  { id: 'country-status', label: 'Country Status', icon: Shield },
];

const ADMIN_EMAILS = ['admin@dreamlife.com', 'admin@thedreamlife.app', 'admin@dreamlife.app'];
const isAdminUser = (u) => !!u && (u.role === 'admin' || u.role === 'super_admin' || ADMIN_EMAILS.includes((u.email || '').toLowerCase()));
const isSuperAdminUser = (u) => !!u && (u.role === 'super_admin' || ADMIN_EMAILS.includes((u.email || '').toLowerCase()));

const AdminPanelPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [countryFilter, setCountryFilter] = useState('all');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [conciergeRequests, setConciergeRequests] = useState([]);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', interest: '', source: 'admin', status: 'new', notes: '' });
  const [billingSummary, setBillingSummary] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistStats, setWaitlistStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [activePartners, setActivePartners] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [countrySettings, setCountrySettings] = useState({});
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningProperty, setAssigningProperty] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [leadPrice, setLeadPrice] = useState(50);

  useEffect(() => {
    // Wait for AuthContext to finish loading before deciding access.
    // AdminRoute already gates the page; this is a defensive secondary check.
    if (user === null || user === undefined) return;
    if (!isAdminUser(user)) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, countryFilter, user]);

  const countryParam = countryFilter !== 'all' ? `?country=${countryFilter}` : '';

  const fetchData = async () => {
    setLoading(true);
    try {
      const handlers = {
        dashboard: async () => { const r = await axios.get(`${API}/admin/dashboard-stats${countryParam}`); setStats(r.data); },
        analytics: async () => { const r = await axios.get(`${API}/admin/analytics/overview`); setAnalyticsData(r.data); },
        waitlist: async () => { const [r1, r2] = await Promise.all([axios.get(`${API}/admin/waitlist`), axios.get(`${API}/admin/waitlist/stats`)]); setWaitlist(r1.data.waitlist || []); setWaitlistStats(r2.data); },
        users: async () => { const r = await axios.get(`${API}/admin/users${countryParam}`); setUsers(r.data.users || r.data || []); },
        leads: async () => { const r = await axios.get(`${API}/admin/leads${countryParam}`); setLeads(r.data); },
        billing: async () => { const [r1, r2] = await Promise.all([axios.get(`${API}/admin/billing/summary`), axios.get(`${API}/admin/partners/active`)]); setBillingSummary(r1.data); setActivePartners(r2.data.partners || []); },
        partners: async () => { const r = await axios.get(`${API}/admin/partner-applications`); setPartners(r.data); },
        properties: async () => { const [r1, r2] = await Promise.all([axios.get(`${API}/admin/properties${countryParam}`), axios.get(`${API}/admin/partners/active`)]); setProperties(r1.data); setActivePartners(r2.data.partners || []); },
        marketplace: async () => { const r = await axios.get(`${API}/admin/marketplace${countryParam}`); setMarketplace(r.data); },
        transactions: async () => { const r = await axios.get(`${API}/admin/transactions${countryParam}`); setTransactions(r.data); },
        concierge: async () => { const r = await axios.get(`${API}/admin/concierge-requests`); setConciergeRequests(r.data.requests || []); },
        countries: async () => {
          const r = await axios.get(`${API}/countries`);
          setAllCountries(r.data.countries || []);
          const settingsMap = {};
          for (const c of (r.data.countries || [])) {
            try { const s = await axios.get(`${API}/countries/${c.code}/settings`); settingsMap[c.code] = s.data.settings || {}; } catch { settingsMap[c.code] = {}; }
          }
          setCountrySettings(settingsMap);
        },
      };
      await (handlers[activeTab] || handlers.dashboard)();
    } catch (error) {
      // Don't redirect on transient errors. AdminRoute already gates the page.
      // 403 here means a specific endpoint denied access — log it but stay on the page.
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 403) {
        toast.error(detail || 'Access denied for this section');
      } else if (status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      } else {
        toast.error(detail || 'Failed to load data');
      }
    } finally { setLoading(false); }
  };

  // Actions
  const handleUpdateUser = async (userId, updates) => { try { await axios.put(`${API}/admin/users/${userId}`, updates); toast.success('User updated'); fetchData(); } catch { toast.error('Failed'); } };
  const handleUpdateLead = async (leadId, updates) => { try { await axios.put(`${API}/admin/leads/${leadId}`, updates); toast.success('Lead updated'); fetchData(); } catch { toast.error('Failed'); } };
  const handleCreateLead = async () => { try { await axios.post(`${API}/admin/leads`, newLead); toast.success('Lead created!'); setShowAddLeadModal(false); setNewLead({ name: '', email: '', phone: '', interest: '', source: 'admin', status: 'new', notes: '' }); fetchData(); } catch { toast.error('Failed'); } };
  const handleUpdatePartner = async (id, status) => { try { await axios.put(`${API}/admin/partner-applications/${id}`, { status }); toast.success(`Partner ${status}`); fetchData(); } catch { toast.error('Failed'); } };
  const handleDeleteProperty = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete(`${API}/admin/properties/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); } };
  const handleDeleteMarketplaceItem = async (id) => { try { await axios.delete(`${API}/admin/marketplace/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); } };
  const handleMarkBilled = async (partnerId) => { try { await axios.post(`${API}/admin/billing/mark-billed/${partnerId}`); toast.success('Marked as billed'); fetchData(); } catch { toast.error('Failed'); } };
  const openAssignModal = (prop) => { setAssigningProperty(prop); setSelectedPartner(prop.assigned_partner_id || ''); setLeadPrice(prop.lead_price || 50); setShowAssignModal(true); };
  const handleAssignPartner = async () => { try { await axios.post(`${API}/admin/properties/${assigningProperty.id}/assign-partner`, { partner_id: selectedPartner, lead_price: Number(leadPrice) }); toast.success('Partner assigned!'); setShowAssignModal(false); fetchData(); } catch { toast.error('Failed'); } };
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const renderContent = () => {
    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gold text-xl">Loading...</div></div>;
    switch (activeTab) {
      case 'dashboard': return <AdminDashboardTab stats={stats} formatCurrency={formatCurrency} setActiveTab={setActiveTab} />;
      case 'analytics': return <AdminAnalyticsTab analyticsData={analyticsData} />;
      case 'waitlist': return <AdminWaitlistTab waitlist={waitlist} waitlistStats={waitlistStats} searchTerm={searchTerm} setSearchTerm={setSearchTerm} fetchData={fetchData} />;
      case 'users': return <AdminUsersTab users={users} searchTerm={searchTerm} setSearchTerm={setSearchTerm} handleUpdateUser={handleUpdateUser} />;
      case 'leads': return <AdminLeadsTab leads={leads} searchTerm={searchTerm} setSearchTerm={setSearchTerm} handleUpdateLead={handleUpdateLead} showAddLeadModal={showAddLeadModal} setShowAddLeadModal={setShowAddLeadModal} newLead={newLead} setNewLead={setNewLead} handleCreateLead={handleCreateLead} selectedLead={selectedLead} setSelectedLead={setSelectedLead} />;
      case 'partners': return <AdminPartnersTab partners={partners} handleUpdatePartner={handleUpdatePartner} />;
      case 'billing': return <AdminBillingTab billingSummary={billingSummary} handleMarkBilled={handleMarkBilled} />;
      case 'properties': return <AdminPropertiesTab properties={properties} handleDeleteProperty={handleDeleteProperty} openAssignModal={openAssignModal} refresh={fetchData} />;
      case 'marketplace': return <AdminMarketplaceTab marketplace={marketplace} handleDeleteMarketplaceItem={handleDeleteMarketplaceItem} />;
      case 'transactions': return <AdminTransactionsTab transactions={transactions} />;
      case 'countries': return <AdminCountriesTab allCountries={allCountries} countrySettings={countrySettings} setCountrySettings={setCountrySettings} fetchData={fetchData} />;
      case 'country-status': return <AdminCountryStatusTab isSuperAdmin={true} />;
      case 'concierge': return <AdminConciergeTab conciergeRequests={conciergeRequests} setConciergeRequests={setConciergeRequests} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      <Nav variant="admin" />
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        <div className="flex gap-8">
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <div className="glass-card p-4 sticky top-28">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${activeTab === tab.id ? 'bg-gold text-black' : 'text-text-secondary hover:bg-white/5'}`} data-testid={`tab-${tab.id}`}>
                    <Icon className="w-5 h-5" />{tab.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1">
            <motion.div key={activeTab} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <h1 className="font-playfair text-3xl font-semibold capitalize">{activeTab}</h1>
                {['dashboard', 'users', 'leads', 'properties', 'transactions', 'marketplace'].includes(activeTab) && (
                  <div className="flex items-center gap-2 glass-card px-4 py-2">
                    <Globe className="w-4 h-4 text-gold" />
                    <span className="text-xs text-text-secondary uppercase tracking-wider">Country</span>
                    <StyledSelect value={countryFilter} onChange={(v) => setCountryFilter(v)} options={[{value:'all',label:'All Countries'},{value:'uae',label:'UAE / Dubai'},{value:'india',label:'India'}]} className="h-8 min-w-[160px] border-0 bg-transparent" data-testid="admin-country-filter" />
                  </div>
                )}
              </div>
              {renderContent()}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-8 max-w-lg w-full">
            <div className="flex justify-between items-center mb-6"><h2 className="font-playfair text-2xl font-semibold">Add New Lead</h2><Button variant="ghost" onClick={() => setShowAddLeadModal(false)}><X className="w-5 h-5" /></Button></div>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} className="bg-white text-black" /></div>
              <div><Label>Email</Label><Input type="email" value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} className="bg-white text-black" /></div>
              <div><Label>Phone</Label><Input value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} className="bg-white text-black" /></div>
              <div><Label>Interest</Label><Input value={newLead.interest} onChange={e => setNewLead({ ...newLead, interest: e.target.value })} className="bg-white text-black" placeholder="e.g., Dubai Marina Penthouse" /></div>
              <div><Label>Status</Label><StyledSelect value={newLead.status} onChange={(v) => setNewLead({ ...newLead, status: v })} options={[{value:'new',label:'New'},{value:'contacted',label:'Contacted'},{value:'closed',label:'Closed'}]} data-testid="new-lead-status-select" /></div>
              <Button onClick={handleCreateLead} disabled={!newLead.name || !newLead.email} className="w-full bg-gold text-black hover:bg-gold-light">Create Lead</Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Assign Partner Modal */}
      {showAssignModal && assigningProperty && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-8 max-w-lg w-full">
            <div className="flex justify-between items-center mb-6"><h2 className="font-playfair text-2xl font-semibold">Assign Partner</h2><Button variant="ghost" onClick={() => setShowAssignModal(false)}><X className="w-5 h-5" /></Button></div>
            <div className="mb-6 p-4 rounded-lg bg-white/5 border border-white/10"><h4 className="font-semibold mb-1">{assigningProperty.title}</h4><p className="text-text-secondary text-sm">{assigningProperty.location}</p><p className="text-gold font-bold mt-2">AED {assigningProperty.price?.toLocaleString()}</p></div>
            <div className="space-y-4">
              <div><Label className="text-white mb-2 block">Select Partner</Label><StyledSelect value={selectedPartner} onChange={(v) => setSelectedPartner(v)} placeholder="-- Select Partner --" options={activePartners.map(p => ({ value: p.id, label: `${p.name || p.company_name || p.email} (${p.role?.replace('_', ' ') || ''})` }))} data-testid="select-partner-dropdown" /></div>
              <div><Label className="text-white mb-2 block">Lead Price (AED)</Label><Input type="number" value={leadPrice} onChange={e => setLeadPrice(e.target.value)} className="bg-white text-black" data-testid="lead-price-input" /><p className="text-text-secondary text-xs mt-1">Amount charged per lead from this property</p></div>
              <Button onClick={handleAssignPartner} disabled={!selectedPartner} className="w-full bg-gold text-black hover:bg-gold-light mt-4" data-testid="confirm-assign-btn"><UserPlus className="w-4 h-4 mr-2" />{assigningProperty.assigned_partner_id ? 'Update Assignment' : 'Assign Partner'}</Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPanelPage;
